import { useCallback, useEffect, useState } from 'react'
import { useTickerApi } from './useTickerApi'

// Season picker + stats freshness, shared by the Standings and Leaders tabs.
// Both read the same scrape (one standings page supplies the division map and
// the team abbreviations the player stats are bucketed by), so they share the
// control that refreshes it.
export default function StatsSource({ seasonId, setSeasonId, onRefreshed, children }) {
  const api = useTickerApi()
  const [seasons, setSeasons] = useState([])
  const [status, setStatus] = useState(null)
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    api.listSeasons()
      .then(r => {
        const list = r?.seasons || []
        setSeasons(list)
        setSeasonId(prev => prev || (list[0]?.id ? String(list[0].id) : ''))
      })
      .catch(e => setError(e.message))
  }, [api, setSeasonId])

  const loadStatus = useCallback(() => {
    if (!seasonId) return
    api.statsStatus(seasonId).then(setStatus).catch(() => setStatus(null))
  }, [api, seasonId])

  useEffect(() => { loadStatus() }, [loadStatus])

  const refresh = async () => {
    setError(null); setBusy('Fetching standings and stats…')
    try {
      const r = await api.fetchStats(seasonId)
      if (r?.error) throw new Error(r.message || r.error)
      setBusy(`${r.teams} teams · ${r.players} skaters`
        + (r.removed_stale ? ` · ${r.removed_stale} stale removed` : '')
        + (r.errors?.length ? ` · ${r.errors.length} page errors` : ''))
      loadStatus()
      onRefreshed?.()
    } catch (e) {
      setError(e.message); setBusy(null); return
    }
    setTimeout(() => setBusy(null), 4000)
  }

  return (
    <>
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex',
        gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Season</span>
          <select value={seasonId} onChange={e => setSeasonId(e.target.value)}>
            {seasons.length === 0 && <option value="">No seasons</option>}
            {seasons.map(s => <option key={s.id} value={s.id}>{s.league} — {s.label}</option>)}
          </select>
        </label>
        {children}
        <span style={{ flex: 1 }} />
        {status && status.teams > 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            {status.teams} teams · {status.players} skaters
            {status.fetched_at && ` · updated ${status.fetched_at.slice(0, 16)} UTC`}
          </span>
        )}
        <button onClick={refresh} disabled={!seasonId || !!busy}
          title="Re-scrape the league standings page and the player stats listing">
          {busy || 'Refresh from league'}
        </button>
      </div>
      {error && (
        <div className="card" style={{ padding: 10, marginBottom: 12, borderLeft: '3px solid #e2574a' }}>
          {error}
        </div>
      )}
    </>
  )
}
